import { ApiExplorerInputType } from '@squareup/dex-types-shared-api-explorer';
import {
  ApiReferenceFieldType,
  ApiReferenceTypeText,
} from '@squareup/dex-ui-shared-api-reference';
import { Box } from '@squareup/dex-ui-shared-base';
import {
  getDefaultForItemInProperty,
  toIgnitionDataType,
} from '@squareup/dex-utils-oas';
import {
  getDateTimeFormat,
  getFieldTypeForItemProperty,
} from '@squareup/dex-utils-shared-api-explorer';
import React, { FC, memo, useCallback } from 'react';

import { ApiExplorerCollapsibleFieldLabel } from './ApiExplorerCollapsibleFieldLabel';
import { ApiExplorerEnumField } from './ApiExplorerEnumField';
import { ApiExplorerFieldLabel } from './ApiExplorerFieldLabel';
import { ApiExplorerObjectField } from './ApiExplorerObjectField';
import { ApiExplorerPrimitiveField } from './ApiExplorerPrimitiveField';
import { ApiExplorerFieldAddButton } from './helpers/ApiExplorerFieldAddButton';
import { ApiExplorerFieldAddButtonSpecialValuesDropdown } from './helpers/ApiExplorerFieldAddButtonSpecialValuesDropdown';
import { useConvertInputValueToMapFieldValue } from './hooks/map-hooks';
import { ApiExplorerStringField } from './primitives/ApiExplorerStringField';
import { ApiExplorerFieldProps } from './props';
import { useApiExplorerFieldContext } from './providers/ApiExplorerFieldProvider';

const strings = {
  addEntry: 'Add entry',
  keyName: 'key',
  keyType: 'string',
  valueName: 'value',
  entryName: 'entry',
};

interface ItemProps extends ApiExplorerFieldProps {
  index: number;
  isLast: boolean;
  onRemove: (index: number) => void;
}

const ApiExplorerMapEntry: FC<ItemProps> = memo(
  ({
    currentValue,
    index,
    property,
    depth = 0,
    path,
    onChange,
    onRemove,
    isLast,
  }) => {
    // We have to call the appropriate APIExplorerField components manually based on the type. Some restrictions
    // to our Array fields because of this are:
    // 1. Only allow primitives, enums, schemas, and any fields
    // 2. Only allow objects
    // We will not allow maps of arrays or maps of maps.
    const newPath = `${path}/${index}`;

    const keyPath = `${newPath}/0`;
    const valuePath = `${newPath}/1`;

    const entryValue = Array.isArray(currentValue) ? currentValue : [];

    const keyVal = entryValue[0] ?? '';
    const valueVal = entryValue[1] ?? null;

    const valueFieldName = strings.valueName;

    const onEntryChanged = useCallback<typeof onChange>(
      (value, entryPath, op) => {
        if (op === 'remove') {
          onRemove(index);
        } else {
          onChange(value, entryPath, op);
        }
      },
      [index, onChange, onRemove]
    );

    const onKeyChanged = useCallback<typeof onChange>(
      (value, keyPath, op) => {
        if (op === 'remove' || value === undefined) {
          onChange('', keyPath);
        } else {
          onChange(value, keyPath, op);
        }
      },
      [onChange]
    );

    const onValueChanged = useCallback<typeof onChange>(
      (value, itemPath, op) => {
        if (!property.itemEntity && (value === undefined || op === 'remove')) {
          // If the value is a primitive, and the user clears it, we will default to the default primitive for that data type
          onChange(
            getDefaultForItemInProperty(property) as ApiExplorerInputType,
            itemPath
          );
        } else {
          onChange(value, itemPath, op);
        }
      },
      [property, onChange]
    );

    const itemFieldType = getFieldTypeForItemProperty(property);
    function getField() {
      switch (itemFieldType) {
        case 'enum':
          return (
            <>
              <ApiExplorerFieldLabel
                name={valueFieldName}
                typeField={
                  <ApiReferenceFieldType
                    labelName={`${property.itemEntity?.name}-${valueFieldName}`}
                    entity={property.itemEntity}
                    isEnum={true}
                  />
                }
              />
              <ApiExplorerEnumField
                // This check does already happen in `getFieldTypeForItemProperty` but Typescript
                // has no available guard for this
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                entity={property.itemEntity!}
                name={valueFieldName}
                path={valuePath}
                currentValue={valueVal}
                depth={depth}
                onChange={onValueChanged}
              />
            </>
          );
        case 'object':
          return (
            <ApiExplorerCollapsibleFieldLabel
              name={valueFieldName}
              typeField={
                <ApiReferenceFieldType
                  labelName={`${property.itemEntity?.name}-${valueFieldName}`}
                  isEntity={true}
                  entity={property.itemEntity}
                />
              }
              currentValue={valueVal}
              path={valuePath}
              depth={depth + 1}
              onChange={onValueChanged}
            >
              <ApiExplorerObjectField
                depth={depth + 1}
                // This check does already happen in `getFieldTypeForItemProperty` but Typescript
                // has no available guard for this
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                entity={property.itemEntity!}
                currentValue={valueVal}
                onChange={onValueChanged}
                path={valuePath}
              />
            </ApiExplorerCollapsibleFieldLabel>
          );
        case 'primitive':
        default:
          return (
            <>
              <ApiExplorerFieldLabel
                name={valueFieldName}
                typeField={
                  <ApiReferenceFieldType
                    labelName={`${property.itemEntity?.name}-${valueFieldName}`}
                    type={property.itemType}
                  />
                }
              />
              <ApiExplorerPrimitiveField
                currentValue={valueVal}
                path={valuePath}
                name={valueFieldName}
                type={property.itemType}
                datetimeFormat={getDateTimeFormat(property)}
                depth={depth}
                onChange={onValueChanged}
                // Since maps must default to some default value for values, that means
                // strings always start off empty. Therefore, we don't need to show it in the dropdown,
                // or show the special filled state for it
                isEmptyStringAllowed={false}
              />
            </>
          );
      }
    }

    const keyField = (
      <>
        <ApiExplorerFieldLabel
          name={strings.keyName}
          typeField={
            <ApiReferenceTypeText>{strings.keyType}</ApiReferenceTypeText>
          }
          margin={{ bottom: '0.5x' }}
        />
        <ApiExplorerStringField
          name="key"
          type={toIgnitionDataType('string')}
          currentValue={keyVal}
          onChange={onKeyChanged}
          path={keyPath}
          isEmptyStringAllowed={false}
        />
      </>
    );

    const entryContent = (
      <>
        {keyField}
        <Box margin={{ top: '2x' }}>{getField()}</Box>
      </>
    );

    return (
      <ApiExplorerCollapsibleFieldLabel
        margin={{ bottom: !isLast ? '2x' : undefined }}
        depth={depth}
        name={`${property.name}[${index}]`}
        typeField={
          <ApiReferenceFieldType
            labelName={`${property.name}-${strings.entryName}`}
            isMap={true}
            isMapEntry={true}
            hasEntity={Boolean(property.itemEntity)}
            itemEntity={property.itemEntity}
            itemType={property.itemType}
            format={property.format}
          />
        }
        path={newPath}
        currentValue={entryValue}
        onChange={onEntryChanged}
      >
        {entryContent}
      </ApiExplorerCollapsibleFieldLabel>
    );
  }
);

ApiExplorerMapEntry.displayName = 'ApiExplorerMapEntry';

interface Props extends ApiExplorerFieldProps {}

const ApiExplorerMapField: FC<Props> = ({
  currentValue,
  property,
  path,
  onChange,
  depth = 0,
}) => {
  const mapFieldValue = useConvertInputValueToMapFieldValue(currentValue);
  const { isNullable } = useApiExplorerFieldContext();

  const onMapEntryAdded = useCallback(() => {
    const newValue = getDefaultForItemInProperty(
      property
    ) as ApiExplorerInputType;

    if (mapFieldValue.length === 0) {
      onChange([['', newValue]], path);
    } else {
      onChange(['', newValue], `${path}/-`);
    }
  }, [mapFieldValue.length, onChange, path, property]);

  const onMapEntryRemoved = useCallback(
    (index: number) => {
      // If there's only one left, remove the whole array
      if (mapFieldValue.length === 1) {
        onChange(undefined, path, 'remove');
      } else {
        onChange(undefined, `${path}/${index}`, 'remove');
      }
    },
    [mapFieldValue.length, onChange, path]
  );

  const addEntryButton = (
    <ApiExplorerFieldAddButton
      onClick={onMapEntryAdded}
      trackingId={`api-explorer-map-field-add-${property.id}`}
      extraValuesTrigger={
        isNullable ? (
          <ApiExplorerFieldAddButtonSpecialValuesDropdown
            trackingId={`api-explorer-map-field-add-extra-values-${property.id}`}
            path={path}
            onChange={onChange}
            currentValue={currentValue}
          />
        ) : null
      }
    >
      {strings.addEntry}
    </ApiExplorerFieldAddButton>
  );

  const entries = mapFieldValue.map((entry, index) => {
    return (
      <ApiExplorerMapEntry
        depth={depth + 1}
        index={index}
        currentValue={entry}
        key={index}
        onChange={onChange}
        path={path}
        property={property}
        onRemove={onMapEntryRemoved}
        isLast={index === mapFieldValue.length - 1}
      />
    );
  });

  return (
    <>
      {entries}
      {addEntryButton}
    </>
  );
};

export { ApiExplorerMapField };
